import React, { useState } from "react";
import {
  Row,
  Col,
  Button,
  Input,
  Label,
  Container,
  Card,
  CardHeader,
  CardBody,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import AdminHeader from "components/Headers/AdminHeader";

// Initial form fields
const initialFields = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
};

const CsvTraineeImport = () => {
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [trainees, setTrainees] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [formData, setFormData] = useState(initialFields);
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    searchName: "",
    searchEmail: "",
  });

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) setFile(droppedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setTrainees([]);
    setEditIndex(null);
    setFormData(initialFields);
  };

  // Parse CSV manually
  const parseCSV = (text) => {
    // \r\n line break - window \n -linux and remove empty row
    const lines = text.split(/\r\n|\n/).filter(Boolean);
    if (lines.length < 2) return []; //csv file should have 1 header row and 1 data row
    const headers = lines[0].split(",").map((h) => h.trim()); //split the header row "," separated  and trim
    return lines.slice(1).map((line, index) => {
      //process same for each row
      const values = line.split(",").map((v) => v.trim());
      const row = {};
      headers.forEach((h, i) => {
        // if value is missing empty string
        row[h] = values[i] || "";
      });
      return {
        srNo: index + 1,
        firstName: row.firstName || "",
        lastName: row.lastName || "",
        email: row.email || "",
        mobile: row.mobile || "",
      };
    });
  };

  const handleImport = () => {
    if (!file) return;
    const reader = new FileReader(); // browser api to reader csv file
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      setTrainees(parsed);
    };
    reader.readAsText(file);
  };

  const handleEdit = (row) => {
    setEditIndex(row.srNo);
    setFormData({
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      mobile: row.mobile,
    });
  };

  const handleSaveEdit = () => {
    setTrainees((prev) =>
      prev.map((t) => (t.srNo === editIndex ? { ...t, ...formData } : t)),
    );
    setEditIndex(null);
    setFormData(initialFields);
  };

  const handleRemoveTrainee = (srNo) => {
    const trainee = trainees.find((t) => t.srNo === srNo);

    showAlert({
      title: "Remove Trainee",
      message: (
        <>
          <p className="mb-1">Are you sure you want to remove this trainee?</p>
          <strong>
            {trainee.firstName} {trainee.lastName}
          </strong>
        </>
      ),
      type: "warning",
      confirmText: "Remove",
      cancelText: "Cancel",
      confirmStyle: "danger",
      onConfirm: () => {
        setTrainees((prev) =>
          prev
            .filter((t) => t.srNo !== srNo)
            .map((t, i) => ({ ...t, srNo: i + 1 })),
        );
      },
    });
  };

  const filteredTrainees = trainees.filter((t) => {
    const nameMatch = `${t.firstName} ${t.lastName}`
      .toLowerCase()
      .includes(searchState.searchName.toLowerCase());

    const emailMatch = t.email
      .toLowerCase()
      .includes(searchState.searchEmail.toLowerCase());

    return nameMatch && emailMatch;
  });

  const columns = [
    {
      Header: "Sr No",
      accessor: "srNo",
    },
    {
      Header: "First Name",
      accessor: "firstName",
      Cell: ({ row, value }) =>
        editIndex === row.original.srNo ? (
          <Input
            value={formData.firstName}
            maxLength={50}
            onChange={(e) =>
              setFormData((p) => ({ ...p, firstName: e.target.value }))
            }
          />
        ) : (
          value
        ),
    },

    {
      Header: "Last Name",
      accessor: "lastName",
      Cell: ({ row, value }) =>
        editIndex === row.original.srNo ? (
          <Input
            value={formData.lastName}
            maxLength={50}
            onChange={(e) =>
              setFormData((p) => ({ ...p, lastName: e.target.value }))
            }
          />
        ) : (
          value
        ),
    },

    {
      Header: "Email",
      accessor: "email",
      Cell: ({ row, value }) =>
        editIndex === row.original.srNo ? (
          <Input
            value={formData.email}
            maxLength={100}
            onChange={(e) =>
              setFormData((p) => ({ ...p, email: e.target.value }))
            }
          />
        ) : (
          value
        ),
    },

    {
      Header: "Mobile",
      accessor: "mobile",
      Cell: ({ row, value }) =>
        editIndex === row.original.srNo ? (
          <Input
            value={formData.mobile}
            onChange={(e) =>
              setFormData((p) => ({ ...p, mobile: e.target.value }))
            }
          />
        ) : (
          value
        ),
    },

    {
      Header: "Action",
      Cell: ({ row }) =>
        editIndex === row.original.srNo ? (
          <Button
            size="sm"
            color="primary"
            className="btn-deep-blue"
            onClick={handleSaveEdit}
          >
            Save
          </Button>
        ) : (
          <>
            <i
              className="fa fa-edit text-primary mr-3 cursor"
              onClick={() => handleEdit(row.original)}
            />
            <i
              className="fa fa-trash text-danger cursor"
              onClick={() => handleRemoveTrainee(row.original.srNo)}
            />
          </>
        ),
    },
  ];

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {};
  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row className="justify-content-center">
          <Col>
            <div className="mb-3 d-flex align-items-center">
              <Button
                color="link"
                className="p-0 d-flex align-items-center text-white cursor"
                onClick={() => navigate(-1)}
              >
                <i className="fa fa-chevron-left mr-2 cursor" />
                Back
              </Button>
            </div>

            <Card>
              <CardHeader>
                <h4 className="mb-0">Import Trainees</h4>
              </CardHeader>

              <CardBody className="p-4">
                <Row>
                  <Col md="7">
                    <Label className="form-control-label mb-2">
                      Select CSV file to Upload
                    </Label>

                    <div
                      className="border rounded p-2 d-flex align-items-center justify-content-between"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleDrop}
                    >
                      {file ? (
                        <div className="d-flex align-items-center">
                          <i className="fa fa-file-csv fa-2x text-muted mr-3" />
                          <div>
                            <div className="font-weight-bold">
                              {file.name}{" "}
                              <span className="text-muted">
                                ({(file.size / 1024).toFixed(2)} KB)
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">Choose CSV file</span>
                      )}

                      <div className="d-flex align-items-center">
                        {file && (
                          <i
                            className="fa fa-times text-danger mr-3 cursor"
                            onClick={handleRemoveFile}
                          />
                        )}

                        <Label className="btn mb-0">
                          Browse
                          <Input
                            type="file"
                            accept=".csv"
                            hidden
                            onChange={handleFileSelect}
                            key={file ? file.name : "empty"}
                          />
                        </Label>
                      </div>
                    </div>

                    <small className="text-muted d-block mt-2">
                      You can upload 1,000 records in one import.
                    </small>

                    <a
                      href={require("../../assets/csv/example.csv")}
                      className="d-inline-flex align-items-center mt-4 text-deep-blue"
                    >
                      <i className="fa fa-download mr-2" />
                      Download a sample CSV file
                    </a>

                    <div className="mt-3 d-flex">
                      <Button
                        color="primary"
                        className="btn-deep-blue cursor mr-2"
                        onClick={handleImport}
                        disabled={!file}
                      >
                        Import Trainee
                      </Button>
                    </div>
                  </Col>

                  <Col md="5">
                    <Label className="form-control-label mb-2">
                      CSV file format requirements
                    </Label>

                    <ul className="pl-3 text-muted">
                      <li>FirstName must be less than 50 characters.</li>
                      <li>LastName must be less than 50 characters.</li>
                      <li>Email must be valid and less than 100 characters.</li>
                      <li>Mobile number must be 10 digits.</li>
                      <li>No extra rows or columns in the CSV file.</li>
                    </ul>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        
        {filteredTrainees.length > 0 && (
          <Row>
            <Col md="12">
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <h4 className="mb-0">Imported Trainees</h4>
                </CardHeader>

                <CardBody>
                  {/* Filters */}
                  <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                    <div className="d-flex flex-wrap align-items-center filter-group">
                      <Input
                        type="text"
                        placeholder="Search by name"
                        className="mr-2 mb-2 filter-input-large"
                        value={searchState.searchName}
                        onChange={(e) =>
                          setSearchState((prev) => ({
                            ...prev,
                            searchName: e.target.value,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                      />

                      <Input
                        type="text"
                        placeholder="Search by email"
                        className="mr-2 mb-2 filter-input-large"
                        value={searchState.searchEmail}
                        onChange={(e) =>
                          setSearchState((prev) => ({
                            ...prev,
                            searchEmail: e.target.value,
                          }))
                        }
                        onKeyDown={handleKeyDown}
                      />

                      <Button
                        color="default"
                        className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                      >
                        <i className="fa fa-search mr-1" />
                        Search
                      </Button>
                    </div>
                  </div>

                  <div className="mt-2 mb-3 mx-1">
                    {filteredTrainees.length ? (
                      <span className="total-records">
                        Total Records: {filteredTrainees.length}
                      </span>
                    ) : null}
                  </div>

                  {/* Table */}
                  <CommonTable
                    columns={columns}
                    data={filteredTrainees.slice(
                      (pageNum - 1) * DEFAULT_RECORDS_PER_PAGE,
                      pageNum * DEFAULT_RECORDS_PER_PAGE,
                    )}
                    defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                    activePage={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      filteredTrainees.length / DEFAULT_RECORDS_PER_PAGE,
                    )}
                    showPaginationBottom
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default CsvTraineeImport;
